import { createContext, useContext, useEffect, type ReactNode } from 'react'
import type { ThemeType } from '@shared'
import { getTheme, generateCSSVariables } from '@shared'
import '../../styles/msb_css.css'

interface ThemeContextType {
  themeType: ThemeType
  theme: ReturnType<typeof getTheme>
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

interface ThemeProviderProps {
  themeType: ThemeType
  children: ReactNode
}

export function ThemeProvider({ themeType, children }: ThemeProviderProps) {
  const theme = getTheme(themeType)

  useEffect(() => {
    // Apply CSS custom properties to the root element
    const root = document.documentElement
    const cssVariables = generateCSSVariables(themeType)
    
    Object.entries(cssVariables).forEach(([property, value]) => {
      root.style.setProperty(property, value)
    })
    
    // Update body class for theme-specific styling
    document.body.className = document.body.className.replace(
      /theme-(therapist|child)/g,
      ''
    )
    document.body.classList.add(`theme-${themeType}`)
  }, [themeType])

  return (
    <ThemeContext.Provider value={{ themeType, theme }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}